import {
  CalendarClock,
  ChevronRight,
  CircleHelp,
  FileText,
  HelpCircle,
  HandCoins,
  Lightbulb,
  ListChecks,
  MessageSquareText,
  ScanText,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AiAssistantContext } from "../../store/aiAssistant.store";
import { isEmeisReceivableEnabled } from "../../utils/emeisModuleFlags";
import { AiWordmark } from "./AiVisuals";

type Icon = React.ComponentType<{ size?: number; className?: string }>;

const CONTRACTS_PATH = "/contracts";
const OBLIGATIONS_PATH = "/obligations-hub/all";
const RECEIVABLES_PATH = "/receivables-engine/all";

/** The module a capability or example belongs to must be switched on too (utils/emeisModuleFlags). */
type Module = "obligation" | "receivable";
const moduleEnabled = (module?: Module) =>
  module === "receivable" ? isEmeisReceivableEnabled() : true;

/** What lawyal.ai does, and where each capability lives. */
const CAPABILITIES: { key: string; label: string; hint: string; icon: Icon; path: string; module?: Module }[] = [
  { key: "qa", label: "Contract Q&A", hint: "Ask anything about an open contract and get the clause it comes from.", icon: MessageSquareText, path: CONTRACTS_PATH },
  { key: "summary", label: "Contract summaries", hint: "Executive, key-terms, commercial, legal and risk summaries.", icon: FileText, path: CONTRACTS_PATH },
  { key: "extract", label: "Data extraction", hint: "Dates, parties and values from the document, compared with the record.", icon: ScanText, path: CONTRACTS_PATH },
  { key: "obligations", label: "Obligation finder", hint: "Obligations and their connections, found in the contract clauses.", icon: ListChecks, path: CONTRACTS_PATH, module: "obligation" },
  { key: "evidence", label: "Evidence review", hint: "Checks uploaded evidence against what the obligation requires.", icon: ShieldCheck, path: OBLIGATIONS_PATH, module: "obligation" },
  { key: "insights", label: "Obligation insights", hint: "What is overdue, blocked or due soon across your obligations.", icon: Lightbulb, path: OBLIGATIONS_PATH, module: "obligation" },
  { key: "receivables", label: "Receivables recovery", hint: "What is owed, what was sent and paid, recovery risks and the contract's payment terms.", icon: HandCoins, path: RECEIVABLES_PATH, module: "receivable" },
];

const EXAMPLES: { key: string; text: string; kind: AiAssistantContext["kind"] }[] = [
  { key: "exampleOwed", text: "How much is still owed on this receivable?", kind: "receivable" },
  { key: "exampleSent", text: "What reminders have been sent to the debtor?", kind: "receivable" },
  { key: "exampleNotice", text: "What notice is needed to terminate this contract?", kind: "contract" },
  { key: "exampleRenewal", text: "When does this contract renew, and how?", kind: "contract" },
  { key: "exampleLiability", text: "Is our liability capped, and at what amount?", kind: "contract" },
  { key: "exampleDue", text: "When is this obligation due and who owns it?", kind: "obligation" },
  { key: "exampleEvidence", text: "What evidence will close this obligation?", kind: "obligation" },
];

const GENERAL_TIPS = [
  { key: "tipOpen", text: "Open a contract or an obligation first: lawyal.ai answers only from the record on screen, and only what you can access." },
  { key: "tipSpecific", text: "Ask one thing at a time and name the topic or clause (“termination”, “clause 12.2”) for sharper answers." },
  { key: "tipSources", text: "Every answer cites its source. Use Open source to jump to the passage in the contract." },
  { key: "tipTab", text: "Summaries, data extraction, suggested obligations and evidence review are in the AI tab of each contract and obligation." },
  { key: "tipReview", text: "Answers are for review, not legal advice: check the clause before relying on them." },
];

/** One-tap questions for an open record, in the manner of an agent shortcut. */
const QUICK_ACTIONS: Record<AiAssistantContext["kind"], { key: string; label: string; question: string; icon: Icon }[]> = {
  contract: [
    { key: "summary", label: "Summarise", question: "Give me a short summary of this contract: parties, purpose, term, value and the main obligations of each party.", icon: FileText },
    { key: "dates", label: "Key dates", question: "List the key dates and deadlines in this contract, with the clause for each.", icon: CalendarClock },
    { key: "risks", label: "Risks to review", question: "Which clauses could be risky for us, and why?", icon: ShieldAlert },
    { key: "duties", label: "Who must do what", question: "List each party's obligations, with the clause each one comes from.", icon: ListChecks },
  ],
  receivable: [
    { key: "status", label: "Where recovery stands", question: "Where does recovery stand: what is owed, what has been recovered and what happens next?", icon: HandCoins },
    { key: "invoices", label: "Overdue invoices", question: "List the overdue invoices with their amounts outstanding and days overdue.", icon: CalendarClock },
    { key: "sent", label: "What was sent", question: "What communications have been sent to the debtor, and did any fail?", icon: MessageSquareText },
    { key: "terms", label: "Contract payment terms", question: "What does the contract say about payment terms, interest and late fees?", icon: FileText },
  ],
  obligation: [
    { key: "explain", label: "Explain it", question: "Explain this obligation in plain words: what must be done, by whom and by when.", icon: HelpCircle },
    { key: "timing", label: "Deadline & timing", question: "When is this obligation due, and what starts the clock?", icon: CalendarClock },
    { key: "evidence", label: "Evidence checklist", question: "What evidence will satisfy this obligation? Give me a checklist.", icon: ShieldCheck },
    { key: "blockers", label: "Blockers", question: "Is anything blocking this obligation, such as a prerequisite that is not complete?", icon: ShieldAlert },
  ],
};

const RECORD_TIPS: Record<AiAssistantContext["kind"], { key: string; text: string }[]> = {
  contract: [
    { key: "tipContractClause", text: "Name a clause or topic for a precise answer, e.g. “What does clause 9 say about indemnity?”" },
    { key: "tipContractTab", text: "For a full summary, extracted data and suggested obligations, open this contract’s AI tab." },
    { key: "tipContractSource", text: "Open source scrolls the document to the passage an answer cites." },
  ],
  receivable: [
    { key: "tipReceivableSources", text: "Answers use this receivable's record, invoices, payments, action plan and recent emails, plus the linked contract when you can see it." },
    { key: "tipReceivableTab", text: "Open this receivable's AI tab for recovery risks and a summary with things to consider next." },
    { key: "tipReceivableNever", text: "AI never contacts the debtor, records a payment or decides a write-off: those stay with the recovery owner." },
  ],
  obligation: [
    { key: "tipObligationClause", text: "Answers use the obligation record, its connected obligations and its source clause when you can see the contract." },
    { key: "tipObligationTab", text: "To check uploaded evidence, use Analyse evidence in this obligation’s AI tab." },
    { key: "tipObligationVerify", text: "AI never verifies evidence or records compliance: that stays in the Verify step." },
  ],
};

type Area = AiAssistantContext["kind"];

/** The module the page belongs to, so the welcome speaks about what is on screen. Null elsewhere (Home, dashboards). */
function areaOf(path: string): Area | null {
  if (path.startsWith("/receivables-engine")) return "receivable";
  if (path.startsWith("/obligations-hub")) return "obligation";
  if (path.startsWith("/contracts")) return "contract";
  return null;
}

/** The obligation form, where obligations can be found in the connected contract with AI. */
const isObligationForm = (path: string) => /^\/obligations-hub\/(new|[^/]+\/edit)\/?$/.test(path);

const AREA_CAPABILITIES: Record<Area, string[]> = {
  contract: ["qa", "summary", "extract", "obligations"],
  obligation: ["obligations", "evidence", "insights"],
  receivable: ["receivables"],
};

const AREA_INTRO: Record<Area, { key: string; text: string }> = {
  contract: { key: "introContract", text: "Open a contract to ask lawyal.ai about it: its clauses, dates, parties and risks. Answers come only from contracts you can access." },
  obligation: { key: "introObligation", text: "Open an obligation to ask about its deadline, owner and evidence. Answers come only from obligations you can access." },
  receivable: { key: "introReceivable", text: "Open a receivable to ask what is owed, what was sent and what happens next. Answers come only from receivables you can access." },
};

const AREA_TIPS: Record<Area, { key: string; text: string }[]> = {
  contract: [
    { key: "tipOpenContract", text: "Open a contract first: lawyal.ai answers from the contract on screen, and cites the clause for each answer." },
    { key: "tipContractTab", text: "For a full summary, extracted data and suggested obligations, open the contract’s AI tab." },
  ],
  obligation: [
    { key: "tipOpenObligation", text: "Open an obligation first: lawyal.ai answers from that obligation, its connected obligations and its source clause." },
    { key: "tipObligationTab", text: "To check uploaded evidence, use Analyse evidence in the obligation’s AI tab." },
  ],
  receivable: [
    { key: "tipOpenReceivable", text: "Open a receivable first: lawyal.ai answers from its invoices, payments, action plan and recent emails." },
    { key: "tipReceivableNever", text: "AI never contacts the debtor, records a payment or decides a write-off: those stay with the recovery owner." },
  ],
};

function SectionTitle({ icon: IconEl, children }: { icon: Icon; children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-accent))]">
      <IconEl size={13} aria-hidden />
      {children}
    </p>
  );
}

function Tips({ items }: { items: { key: string; text: string }[] }) {
  const { t } = useTranslation("ai");
  return (
    <section className="space-y-1.5">
      <SectionTitle icon={Lightbulb}>{t("guide.tips", { defaultValue: "Tips" })}</SectionTitle>
      <ul className="list-disc space-y-1 pl-5 text-xs leading-relaxed text-[rgb(var(--text-secondary))]">
        {items.map((tip) => (
          <li key={tip.key}>{t(`guide.${tip.key}`, { defaultValue: tip.text })}</li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Shown when no record is open: what lawyal.ai can do, questions to try and
 * how to get good answers. A question picked here waits for a record to be
 * opened; it is never sent on its own.
 */
export function AssistantWelcome({ onPickExample }: { onPickExample: (question: string) => void }) {
  const { t } = useTranslation("ai");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const area = areaOf(pathname);
  const onForm = area === "obligation" && isObligationForm(pathname);
  const intro = onForm
    ? { key: "introObligationForm", text: "Adding obligations to a contract? Use Add Obligations using AI on this form to find them in the contract’s clauses, then review each one before adding it." }
    : area
      ? AREA_INTRO[area]
      : { key: "noContext", text: "Open a contract or an obligation to ask AI about it. The assistant only answers from records you can access." };
  const capabilities = CAPABILITIES.filter((c) => moduleEnabled(c.module) && (!area || AREA_CAPABILITIES[area].includes(c.key)));
  const examples = EXAMPLES.filter(
    (e) => moduleEnabled(e.kind === "obligation" || e.kind === "receivable" ? e.kind : undefined) && (!area || e.kind === area),
  );
  const tips = area
    ? [...AREA_TIPS[area], GENERAL_TIPS.find((g) => g.key === "tipSpecific")!, GENERAL_TIPS.find((g) => g.key === "tipReview")!]
    : GENERAL_TIPS;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[rgb(var(--border))] bg-[rgb(var(--card))] px-4 py-6 text-center">
        <AiWordmark height={26} className="text-[rgb(var(--text-accent))]" />
        <p className="text-sm font-semibold text-[rgb(var(--text-primary))]">
          {t("guide.welcomeTitle", { defaultValue: "Hi, I’m lawyal.ai" })}
        </p>
        <p className="text-xs text-[rgb(var(--text-secondary))]">
          {intro.key === "noContext"
            ? t("assistant.noContext", { defaultValue: intro.text })
            : t(`guide.${intro.key}`, { defaultValue: intro.text })}
        </p>
      </div>

      <section className="space-y-2">
        <SectionTitle icon={Sparkles}>{t("guide.capabilities", { defaultValue: "What lawyal.ai can do" })}</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {capabilities.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => navigate(c.path)}
              title={t(`guide.capability.${c.key}Hint`, { defaultValue: c.hint })}
              className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2 text-xs font-semibold text-[rgb(var(--text-primary))] transition-colors hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--text-accent))]/[0.06]"
            >
              <c.icon size={15} className="text-[rgb(var(--text-accent))]" aria-hidden />
              {t(`guide.capability.${c.key}`, { defaultValue: c.label })}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <SectionTitle icon={CircleHelp}>{t("guide.tryAsking", { defaultValue: "Try asking" })}</SectionTitle>
        <div className="flex flex-wrap gap-1.5">
          {examples.map((e) => {
            const text = t(`guide.${e.key}`, { defaultValue: e.text });
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => {
                  onPickExample(text);
                  navigate(e.kind === "contract" ? CONTRACTS_PATH : e.kind === "receivable" ? RECEIVABLES_PATH : OBLIGATIONS_PATH);
                }}
                className="rounded-full border border-[rgb(var(--text-accent))]/35 bg-[rgb(var(--text-accent))]/[0.06] px-3 py-1.5 text-left text-xs text-[rgb(var(--text-primary))] transition-colors hover:border-[rgb(var(--text-accent))] hover:bg-[rgb(var(--text-accent))]/[0.12]"
              >
                {text}
              </button>
            );
          })}
        </div>
      </section>

      <Tips items={tips} />
    </div>
  );
}

/** A question picked before any record was open, now ready to ask about this one. */
export function PendingQuestion({
  question,
  recordLabel,
  onAsk,
  onDismiss,
}: {
  question: string;
  recordLabel: string;
  onAsk: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation("ai");
  return (
    <div className="rounded-xl border border-[rgb(var(--text-accent))]/40 bg-[rgb(var(--text-accent))]/[0.06] px-3.5 py-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-accent))]">
          {t("guide.pendingTitle", { defaultValue: "Your question" })}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("guide.pendingDismiss", { defaultValue: "Dismiss this question" })}
          className="rounded-md p-0.5 text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))]"
        >
          <X size={13} />
        </button>
      </div>
      <p className="mt-1 text-sm text-[rgb(var(--text-primary))]">{question}</p>
      <button
        type="button"
        onClick={onAsk}
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--accent-fill))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--accent-ink))] hover:opacity-90"
      >
        <Send size={13} aria-hidden />
        {t("guide.pendingAsk", { defaultValue: "Ask this about {{record}}", record: recordLabel })}
      </button>
    </div>
  );
}

/** Quick actions for the open record: common questions asked in one tap. */
export function QuickActions({ kind, onAsk, disabled }: { kind: AiAssistantContext["kind"]; onAsk: (question: string) => void; disabled?: boolean }) {
  const { t } = useTranslation("ai");
  return (
    <section className="space-y-2">
      <SectionTitle icon={Sparkles}>{t("guide.quickActions", { defaultValue: "Quick actions" })}</SectionTitle>
      <div className="grid grid-cols-2 gap-2">
        {QUICK_ACTIONS[kind].map((a) => (
          <button
            key={a.key}
            type="button"
            disabled={disabled}
            onClick={() => onAsk(t(`guide.quick.${kind}.${a.key}Question`, { defaultValue: a.question }))}
            className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2.5 text-left text-xs font-semibold text-[rgb(var(--text-primary))] transition-colors hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--text-accent))]/[0.06] disabled:opacity-50"
          >
            <a.icon size={15} className="shrink-0 text-[rgb(var(--text-accent))]" aria-hidden />
            {t(`guide.quick.${kind}.${a.key}`, { defaultValue: a.label })}
          </button>
        ))}
      </div>
    </section>
  );
}

/** Suggested questions for the open record, as a list to tap. */
export function TryAsking({ prompts, onAsk }: { prompts: string[]; onAsk: (question: string) => void }) {
  const { t } = useTranslation("ai");
  return (
    <section className="space-y-2">
      <SectionTitle icon={CircleHelp}>{t("guide.tryAsking", { defaultValue: "Try asking" })}</SectionTitle>
      <div className="grid gap-1.5">
        {prompts.map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => onAsk(label)}
            className="group flex items-center justify-between gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2 text-left text-xs text-[rgb(var(--text-primary))] transition-colors hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--text-accent))]/[0.06]"
          >
            <span className="min-w-0">{label}</span>
            <ChevronRight
              size={14}
              aria-hidden
              className="shrink-0 text-[rgb(var(--text-muted))] transition-transform group-hover:translate-x-0.5 group-hover:text-[rgb(var(--text-accent))]"
            />
          </button>
        ))}
      </div>
    </section>
  );
}

/** Tips for asking about the open record. */
export function RecordTips({ kind }: { kind: AiAssistantContext["kind"] }) {
  return <Tips items={RECORD_TIPS[kind]} />;
}
